import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema, type Options as SanitizeSchema } from 'rehype-sanitize';
import { visit } from 'unist-util-visit';
import type { Root } from 'hast';
import type { Plugin, PluggableList } from 'unified';
import { REFLEX_SCAN_SCHEME } from './content-scanner-dispatch';

/**
 * Cheap pre-check for "is there any HTML in here at all". A tag always starts
 * with `<` followed by a letter, `/`, or `!`, so anything else (`a < b`, a
 * generic `Foo<Bar>` inside prose) skips the raw pipeline entirely.
 */
const HTML_TAG_START = /<[a-z!/]/i;

/**
 * Does `content` contain markup that needs the raw-HTML pipeline?
 *
 * Without HTML the parse is unchanged: no reparse through parse5, no sanitize
 * walk. That keeps the common case — streamed agent prose, chat messages —
 * exactly as fast as it was before inline HTML was supported.
 */
export function containsInlineHtml(content: string): boolean {
  return HTML_TAG_START.test(content);
}

/**
 * CSS properties an author may set from inline HTML. Deliberately tiny and
 * presentational: colored text (the reason this exists — GitHub renders
 * `<span style="color:#2563eb">` and readers paste that markdown here),
 * emphasis, and alignment. Anything that can move, cover, or size an element
 * — `position`, `display`, `width`, `content`, `transform` — stays out, so a
 * span can never overlay the UI around it.
 */
const ALLOWED_STYLE_PROPERTIES = new Set([
  'background-color',
  'color',
  'font-style',
  'font-weight',
  'text-align',
  'text-decoration',
  'text-decoration-color',
]);

/**
 * Characters a declaration value may use: colors (`#2563eb`, `rgb(37 99 235)`,
 * `hsl(...)`, `red`), keywords, numbers, and percentages. The excluded ones do
 * the damage — `url(...)` needs `:` or `/`, CSS escapes need `\`, and comment
 * tricks need `/*`.
 */
const SAFE_STYLE_VALUE = /^[a-z0-9#%.,()\s-]+$/i;

/** Longest declaration value we keep; real colors are far shorter. */
const MAX_STYLE_VALUE_LENGTH = 64;

/**
 * Keep only the allowlisted declarations of an inline `style`, returning the
 * rebuilt value (or `undefined` when nothing survives).
 */
export function filterInlineStyle(style: string): string | undefined {
  const kept: string[] = [];
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon === -1) continue;
    const property = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();
    if (!ALLOWED_STYLE_PROPERTIES.has(property)) continue;
    if (value.length === 0 || value.length > MAX_STYLE_VALUE_LENGTH) continue;
    if (!SAFE_STYLE_VALUE.test(value)) continue;
    kept.push(`${property}: ${value}`);
  }
  return kept.length > 0 ? kept.join('; ') : undefined;
}

/**
 * rehype plugin: rewrite every `style` attribute down to
 * {@link ALLOWED_STYLE_PROPERTIES}, dropping the attribute when nothing is
 * left. The sanitize schema lets `style` through as an opaque string; this is
 * what makes that safe, and it runs after sanitize so it sees the final tree.
 */
export const rehypeSafeInlineStyle: Plugin<[], Root> = () => (tree) => {
  visit(tree, 'element', (node) => {
    const style = node.properties?.style;
    if (typeof style !== 'string') return;
    const filtered = filterInlineStyle(style);
    if (filtered === undefined) delete node.properties.style;
    else node.properties.style = filtered;
  });
};

/**
 * Tags GitHub's own allowlist has that `hast-util-sanitize`'s "GitHub-style"
 * default schema is missing. The default is close to GitHub's but not equal to
 * it, and the difference is all semantic inline markup people actually write
 * in READMEs: `<mark>` highlights, `<abbr title>` glossary terms, `<small>`
 * fine print, `<figure>`/`<figcaption>` around an image, `<caption>` on a
 * table.
 *
 * Source of truth: the `DEFAULT_CONFIG` allowlist in `html-pipeline`'s
 * sanitization filter, which is what renders markdown on github.com.
 */
const GITHUB_EXTRA_TAG_NAMES = [
  'abbr',
  'bdo',
  'caption',
  'cite',
  'dfn',
  'figcaption',
  'figure',
  'mark',
  'small',
  'time',
  'wbr',
];

/**
 * Attributes GitHub allows globally that the default schema drops.
 *
 * `rel` and `target` are deliberately *not* here even though GitHub allows
 * `rel`: the link components set their own `target="_blank"` with
 * `rel="noopener noreferrer"`, and letting authored markup near either one
 * invites reverse tabnabbing for no expressive gain.
 */
const GITHUB_EXTRA_GLOBAL_ATTRIBUTES = ['ariaHidden', 'role'];

type AttributeList = NonNullable<SanitizeSchema['attributes']>[string];

/** A tag's default allowlist entry with `extra` attribute names merged in. */
function withAttributes(tag: string, extra: string[]): AttributeList {
  return [...(defaultSchema.attributes?.[tag] ?? []), ...extra];
}

/**
 * GitHub's markdown allowlist, plus the three things this app needs on top:
 * the tags and attributes above that bring the default schema up to GitHub
 * parity, inline `style` (narrowed by {@link rehypeSafeInlineStyle}, where we
 * are deliberately a superset — GitHub drops `style` outright, which is what
 * turns a colored swatch into a black one), and the internal `reflex-scan:`
 * scheme our content-scanner links are built on.
 */
const schema: SanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), ...GITHUB_EXTRA_TAG_NAMES],
  attributes: {
    ...defaultSchema.attributes,
    // GitHub takes `type` globally, which is safe for them because they strip
    // `<input>` entirely and add task-list checkboxes afterwards. We have to
    // keep `input` (that is how GFM task lists reach the DOM), so `type` is
    // granted per list element instead — enough for `<ol type="a">`, never
    // enough to turn a checkbox into a text field.
    ol: withAttributes('ol', ['type']),
    ul: withAttributes('ul', ['type']),
    li: withAttributes('li', ['type']),
    // GitHub allows `loading` on images; lazy README banners rely on it.
    img: withAttributes('img', ['loading']),
    '*': [...(defaultSchema.attributes?.['*'] ?? []), ...GITHUB_EXTRA_GLOBAL_ATTRIBUTES, 'style'],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), REFLEX_SCAN_SCHEME.replace(':', '')],
  },
};

/**
 * The raw-HTML pipeline, in order: parse the raw nodes react-markdown left in
 * the tree, sanitize the result against a GitHub-parity allowlist, then narrow
 * whatever `style` survived. Sanitizing is not optional — this content is
 * agent- and user-authored, so unsanitized `rehype-raw` would be stored XSS.
 *
 * Caller-supplied rehype plugins run *after* these, so a plugin that adds
 * attributes (`rehype-slug`'s heading ids) is not clobbered by the sanitizer.
 */
export const INLINE_HTML_REHYPE_PLUGINS: PluggableList = [
  rehypeRaw,
  [rehypeSanitize, schema],
  rehypeSafeInlineStyle,
];
